/**
 * tema.ts
 *
 * NE İŞE YARAR : Tasarım dilinin **tek kaynağı** — renk, yazı, boşluk, köşe, gölge
 * KAYNAK SPEC  : 15-arayuz-standardi.md §4 (tasarım dili) · §4.1 renk · §4.2 yazı
 *                §4.3 boşluk · §4.4 köşe ve gölge · §4.5 koyu tema
 * BAĞLI OLDUĞU : @mantine/core
 * KULLANAN     : src/main.tsx (`MantineProvider`) — ve dolaylı olarak her bileşen
 * DİKKAT       : 1) BİLEŞEN İÇİNE RENK YAZILMAZ (`15` §4). Bir bileşende sabit renk
 *                   bulunması, koyu temayı ileride açmayı imkânsız kılar: değişkenler
 *                   değişir, sabitler değişmez. Kural bu dosyanın var olma sebebidir.
 *                2) ANA RENK **SOĞUK VE DOYGUNLUĞU DÜŞÜK** seçildi (`15` §4.1). Parlak
 *                   bir ana renk yönetim panelinde yorucudur ve veri okunurluğunu
 *                   düşürür; gün boyu tabloya bakan bir kullanıcı için bu bir konfor
 *                   ayrıntısı değil, hata oranı meselesidir.
 *                3) NÖTR GRİLER TAM NÖTR DEĞİL (`15` §4.1): ana renge doğru çok hafif
 *                   kaydırıldı. Saf gri *"seçilmemiş"* hissi verir — tasarlanmamış gibi
 *                   görünür.
 *                4) DURUM RENKLERİ AKSİYON RENGİ DEĞİLDİR. Yeşil bir buton *"kaydet"*
 *                   demez; yeşil yalnız *"başarılı"* demektir. Bu yüzden `primaryColor`
 *                   yalnız `ana`dır ve durum renkleri buton varsayılanı olarak
 *                   kullanılmaz.
 *                5) ÖLÇEKLER KAPALI KÜMEDİR. Yazı 6 basamak (12/14/16/20/24/32), boşluk
 *                   4'ün katları (4/8/12/16/24/32/48), köşe **tek değer** (4 px). Ara
 *                   değer yok: *"biraz daha büyük"* diye eklenen her ara değer, ölçeği
 *                   ölçek olmaktan çıkarır.
 *                6) GÖLGE YALNIZ YÜZEN KATMANDA (`15` §4.4): modal, açılır menü,
 *                   bildirim. Kart ve tablo gölge almaz; ayrım için **çizgi** kullanılır.
 *                   Mantine'in `sm`/`md` gölgeleri bu yüzden sıfırlandı.
 *                7) KOYU TEMA FAZ 1'DE KAPALI (`15` §4.5) ama **altyapısı hazır**:
 *                   `forceColorScheme` ile kapatılıyor, renkler değişken olarak
 *                   tanımlanıyor. Açılması ikinci bir değer kümesi eklemekten ibaret
 *                   olacak — ama her ekranın koyu temada gözden geçirilmesi ayrı bir iş.
 *                8) `tabular-nums` SAYI SÜTUNLARI İÇİN ZORUNLU (`15` §4.2). Orantılı
 *                   rakamla yazılan bir doluluk tablosunda rakamlar alt alta hizalanmaz
 *                   ve göz sütunu tarayamaz.
 * SON GÜNCELLEME: 2026-08-31 04:35:00
 */
import { createTheme, rem, type MantineColorsTuple } from '@mantine/core';

/**
 * Ana renk — soğuk, doygunluğu düşük mavi-gri (başlıktaki not 2).
 *
 * Mantine on basamak ister; 6. basamak (`[5]`) birincil tondur.
 */
const ANA: MantineColorsTuple = [
  '#eef2f6',
  '#dbe3ec',
  '#b6c6d9',
  '#8ea7c5',
  '#6d8db4',
  '#587ca9',
  '#4b73a5',
  '#3c6191',
  '#325682',
  '#254a73',
];

/**
 * Nötr ölçek — ana renge doğru hafif kaydırılmış gri (başlıktaki not 3).
 *
 * Zemin, kenarlık ve ikincil metin buradan gelir.
 */
const NOTR: MantineColorsTuple = [
  '#f6f7f9',
  '#eceef2',
  '#dcdfe6',
  '#c8ccd6',
  '#aeb4c2',
  '#8b93a5',
  '#6b7386',
  '#4e5568',
  '#363c4b',
  '#22262f',
];

/** Durum renkleri — **yalnız** durum bildirimi için (başlıktaki not 4). */
const BASARI: MantineColorsTuple = [
  '#e9f6ee', '#d3ecdd', '#a6d9bb', '#78c599', '#55b57e',
  '#41ab6d', '#36a464', '#288f54', '#1f7f49', '#116d3c',
];

const UYARI: MantineColorsTuple = [
  '#fdf4e7', '#f8e8ce', '#f0cf9c', '#e8b466', '#e29e3a',
  '#df911e', '#de8a10', '#c57705', '#af6900', '#985a00',
];

const HATA: MantineColorsTuple = [
  '#fdeceb', '#f7d7d6', '#eeadab', '#e4807d', '#dc5b57',
  '#d8443f', '#d63a34', '#be2c27', '#aa2621', '#951d19',
];

const BILGI: MantineColorsTuple = [
  '#e8f3fb', '#d5e5f2', '#a9c9e4', '#7bacd6', '#5794cb',
  '#4086c5', '#337fc3', '#256dac', '#1a619b', '#025489',
];

/**
 * Yazı ölçeği — 6 basamak, ara değer yok (`15` §4.2).
 *
 * Gövde 14 px: yönetim panelinde standart. 16 px *"daha okunaklı"* görünür ama ekran
 * başına gösterilen satır sayısını düşürür ve bu panelde satır sayısı bir işlevdir.
 */
export const YAZI_OLCEGI = {
  xs: rem(12),
  sm: rem(14),
  md: rem(16),
  lg: rem(20),
  xl: rem(24),
  xxl: rem(32),
} as const;

/** Boşluk ölçeği — 4'ün katları (`15` §4.3). */
export const BOSLUK = {
  xs: rem(4),
  sm: rem(8),
  md: rem(12),
  lg: rem(16),
  xl: rem(24),
  xxl: rem(32),
  xxxl: rem(48),
} as const;

/** Tablo satır yüksekliği — 40 px (`15` §4.3). Alçağı okunmaz, yükseği ekranı harcar. */
export const TABLO_SATIR_YUKSEKLIGI = 40;

/**
 * Arayüz yazı tipi ailesi.
 *
 * Sistem yazı tipleri kullanılıyor ve bu bilinçli: Türkçe karakterleri (`İ ı Ğ ğ Ş ş
 * Ç ç Ö ö Ü ü`) tam destekleyen, indirilmeyi beklemeyen ve her ağırlıkta doğru görünen
 * bir aile. Dışarıdan bir yazı tipi eklemek, ilk yüklemeye ağ gecikmesi ekler ve
 * yüklenene kadar metni ya gizler ya sıçratır (`15` §4.2).
 */
const YAZI_AILESI =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const tema = createTheme({
  primaryColor: 'ana',
  primaryShade: { light: 6, dark: 5 },

  colors: {
    ana: ANA,
    notr: NOTR,
    basari: BASARI,
    uyari: UYARI,
    hata: HATA,
    bilgi: BILGI,
  },

  fontFamily: YAZI_AILESI,
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, "Cascadia Mono", Consolas, monospace',
  fontSizes: YAZI_OLCEGI,
  lineHeights: {
    // Gövde 1.5, başlık 1.2 (`15` §4.2).
    xs: '1.5',
    sm: '1.5',
    md: '1.5',
    lg: '1.4',
    xl: '1.2',
  },

  headings: {
    fontFamily: YAZI_AILESI,
    fontWeight: '600',
    sizes: {
      h1: { fontSize: YAZI_OLCEGI.xxl, lineHeight: '1.2' },
      h2: { fontSize: YAZI_OLCEGI.xl, lineHeight: '1.2' },
      h3: { fontSize: YAZI_OLCEGI.lg, lineHeight: '1.2' },
      h4: { fontSize: YAZI_OLCEGI.md, lineHeight: '1.2' },
      h5: { fontSize: YAZI_OLCEGI.sm, lineHeight: '1.2' },
      h6: { fontSize: YAZI_OLCEGI.xs, lineHeight: '1.2' },
    },
  },

  spacing: BOSLUK,

  // Köşe TEK DEĞER (başlıktaki not 5). Mantine ölçeği ister; hepsi aynı verilir ki
  // bileşen hangisini seçerse seçsin sonuç değişmesin.
  radius: {
    xs: rem(4),
    sm: rem(4),
    md: rem(4),
    lg: rem(4),
    xl: rem(4),
  },
  defaultRadius: 'sm',

  // Gölge yalnız yüzen katmanda (başlıktaki not 6): `xs`–`sm` sıfırlandı, `md` ve
  // üstü modal/açılır menü/bildirim içindir.
  shadows: {
    xs: 'none',
    sm: 'none',
    md: '0 4px 12px rgba(34, 38, 47, 0.12)',
    lg: '0 8px 24px rgba(34, 38, 47, 0.16)',
    xl: '0 16px 40px rgba(34, 38, 47, 0.20)',
  },

  // Odak halkası ana renktedir (`15` §4.1) ve **kaldırılmaz**: klavyeyle gezen kullanıcı
  // nerede olduğunu ancak buradan bilir (`15` §13).
  focusRing: 'auto',

  other: {
    tabloSatirYuksekligi: TABLO_SATIR_YUKSEKLIGI,
    /** Sayı ve tarih sütunlarında zorunlu (başlıktaki not 8). */
    sayiHizalama: { fontVariantNumeric: 'tabular-nums' },
  },
});
